import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Eye, Package, Loader2, FileWarning, FileCheck2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import JSZip from "jszip";
import { drawingsForRooms, fetchDrawingBytes } from "../../lib/submittalDrawings";
import { downloadProjectReport, uploadProjectReport } from "../../lib/reportsApi";
import { generateSubmittalCoverPdf } from "../../lib/pdfReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories of static reference files attached per model. Each model in the
// project will get a row per category. Files are stub PDFs generated on the
// fly today; once the admin uploader is wired we'll swap to real uploads.
const STATIC_CATEGORIES = [
  { key: "wiring", label: "Wiring Diagrams" },
  { key: "pid", label: "P&ID Diagrams" },
  { key: "brochure", label: "Product Brochures" },
  { key: "iom", label: "Installation & Maintenance Manuals" },
  { key: "service", label: "Service Manuals" },
  { key: "cert", label: "Certifications" },
];

// Build a small placeholder PDF that clearly marks itself as a stub so the
// engineer reviewing the merged package knows what's still pending real
// content from the admin uploader.
function buildStubPdf({ project, modelLabel, categoryLabel }) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, 210, 30, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.text("ZAMBA", 20, 19);
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(project?.name || "Project", 195, 19, { align: "right" });

  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(28);
  pdf.setFont("helvetica", "bold");
  pdf.text(categoryLabel, 20, 70);
  pdf.setFontSize(18);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(71, 85, 105);
  pdf.text(modelLabel, 20, 82);

  pdf.setFontSize(11);
  pdf.setTextColor(220, 38, 38);
  pdf.text(
    "PLACEHOLDER — replace via admin uploader",
    20, 110
  );

  pdf.setTextColor(71, 85, 105);
  pdf.setFontSize(10);
  pdf.text(
    [
      "This sheet is a placeholder generated by the ZAMBA Product Selector.",
      "The published " + categoryLabel + " for " + modelLabel + " will replace this page once",
      "the admin uploads the source PDF.",
      "",
      "Contact ZAMBA HVAC at www.zambahvac.com if you need this document urgently.",
    ],
    20, 125,
    { maxWidth: 170 }
  );
  return pdf.output("blob");
}

// ----------------------------------------------------------------------------
// pdf-lib helpers — Title page + TOC drawn natively so we get crisp text and
// accurate cross-references. Layout mirrors the existing submittal cover/TOC
// in /lib/pdfReport.js so the look stays consistent across products.
// ----------------------------------------------------------------------------

const DARK = rgb(0.058, 0.090, 0.165);  // #0f172a slate-900
const GRAY = rgb(0.46, 0.50, 0.55);     // #75808c slate-500-ish
const LIGHT = rgb(0.59, 0.66, 0.72);    // slate-400
const EMERALD = rgb(0.024, 0.588, 0.412); // #069669 emerald-600

function fmtDateLong() {
  return new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

function drawTitlePage(page, project, helv, helvBold) {
  const w = page.getWidth();
  const h = page.getHeight();

  // Top brand bar
  page.drawRectangle({
    x: 0, y: h - 90, width: w, height: 90, color: DARK,
  });
  page.drawText("ZAMBA", {
    x: 50, y: h - 55, size: 28, font: helvBold, color: rgb(1, 1, 1),
  });
  page.drawText("Product Selector", {
    x: 50, y: h - 75, size: 11, font: helv, color: rgb(0.78, 0.85, 0.92),
  });
  // Emerald accent stripe under the bar
  page.drawRectangle({
    x: 0, y: h - 95, width: w, height: 5, color: EMERALD,
  });

  // Big title — centered vertically in the page body
  const titleSize = 40;
  const title = "Submittal Package";
  const titleWidth = helvBold.widthOfTextAtSize(title, titleSize);
  page.drawText(title, {
    x: (w - titleWidth) / 2,
    y: h / 2 + 100,
    size: titleSize,
    font: helvBold,
    color: DARK,
  });

  // Project details block — left-aligned, big and readable.
  const cx = 80;
  let cy = h / 2 + 30;
  const labelFor = (label, value) => {
    page.drawText(label.toUpperCase(), {
      x: cx, y: cy, size: 9, font: helvBold, color: GRAY,
    });
    page.drawText(value || "—", {
      x: cx, y: cy - 18, size: 18, font: helvBold, color: DARK,
    });
    cy -= 50;
  };
  labelFor("Project", project?.name || "Project");
  if (project?.number) labelFor("Project Number", String(project.number));
  if (project?.client) labelFor("Client", project.client);
  if (project?.address) labelFor("Site", project.address);
  labelFor("Date", fmtDateLong());

  // Footer line
  page.drawLine({
    start: { x: 50, y: 60 }, end: { x: w - 50, y: 60 },
    thickness: 0.6, color: DARK,
  });
  page.drawText("ZAMBA HVAC  ·  www.zambahvac.com", {
    x: 50, y: 42, size: 9, font: helv, color: GRAY,
  });
  const stamp = `Generated ${fmtDateLong()}`;
  const sw = helv.widthOfTextAtSize(stamp, 9);
  page.drawText(stamp, {
    x: w - 50 - sw, y: 42, size: 9, font: helv, color: GRAY,
  });
}

function drawTocPage(page, project, sections, helv, helvBold) {
  const w = page.getWidth();
  const h = page.getHeight();
  const margin = 50;

  // Top border line + project header
  page.drawLine({
    start: { x: margin, y: h - margin - 12 },
    end:   { x: w - margin, y: h - margin - 12 },
    thickness: 0.8, color: DARK,
  });
  page.drawText("ZAMBA", {
    x: margin, y: h - margin - 4,
    size: 14, font: helvBold, color: DARK,
  });
  const projectName = project?.name || "Project";
  const projWidth = helv.widthOfTextAtSize(projectName, 10);
  page.drawText(projectName, {
    x: w - margin - projWidth, y: h - margin - 4,
    size: 10, font: helv, color: GRAY,
  });

  // Heading
  page.drawText("Table of Contents", {
    x: margin, y: h - margin - 48,
    size: 24, font: helvBold, color: DARK,
  });

  // Entries
  const rowH = 24;
  const labelSize = 12;
  const pageNumSize = 12;
  const labelX = margin + 32;
  let y = h - margin - 90;

  sections.forEach((s, idx) => {
    const num = String(idx + 1).padStart(2, "0");
    page.drawText(num, {
      x: margin, y, size: pageNumSize, font: helvBold, color: DARK,
    });
    page.drawText(s.label, {
      x: labelX, y, size: labelSize, font: helv, color: DARK,
    });
    const pageStr = String(s.page);
    const pageW = helvBold.widthOfTextAtSize(pageStr, pageNumSize);
    page.drawText(pageStr, {
      x: w - margin - pageW, y, size: pageNumSize, font: helvBold, color: DARK,
    });
    // Leader dots
    const labelWidth = helv.widthOfTextAtSize(s.label, labelSize);
    const dotsStart = labelX + labelWidth + 8;
    const dotsEnd = w - margin - pageW - 8;
    if (dotsEnd > dotsStart) {
      const dotW = helv.widthOfTextAtSize(".", labelSize);
      const dotCount = Math.floor((dotsEnd - dotsStart) / (dotW * 2.2));
      if (dotCount > 0) {
        page.drawText(" .".repeat(dotCount).trim(), {
          x: dotsStart, y, size: labelSize, font: helv, color: LIGHT,
        });
      }
    }
    y -= rowH;
  });

  // Footer
  const fStr = `Generated ${fmtDateLong()}`;
  const fW = helv.widthOfTextAtSize(fStr, 8);
  page.drawText(fStr, {
    x: (w - fW) / 2, y: 30, size: 8, font: helv, color: GRAY,
  });
}

// Models present in the project — derived from rooms list. We dedupe and
// preserve the order rooms were entered so the engineer sees a familiar layout.
function modelsInProject(rooms = []) {
  const seen = new Set();
  const out = [];
  for (const r of rooms) {
    const m = r?.unit_model;
    if (!m || seen.has(m)) continue;
    seen.add(m);
    out.push(m);
  }
  return out;
}

export default function SubmittalPackageTab({
  project, rooms = [], results = [], configurations = [], isSI = false,
  reports = {}, onReportGenerated, onJumpToStep,
}) {
  const projectModels = useMemo(() => modelsInProject(rooms), [rooms]);

  // Pull the admin-uploaded asset list once on mount. Endpoint is gated
  // behind authentication but available to any logged-in user; admin-only
  // gating is enforced on POST/DELETE, not GET.
  const [uploadedAssets, setUploadedAssets] = useState([]); // raw asset rows from /api/model-assets
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/model-assets`)
      .then((resp) => {
        if (alive) setUploadedAssets(resp.data || []);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          toast.warning("Session expired — sign in again to load product documents");
        } else {
          // Non-fatal: tab still works with stubs. Log only.
          // eslint-disable-next-line no-console
          console.warn("Could not load uploaded assets, falling back to stubs:", err);
        }
      });
    return () => { alive = false; };
  }, []);

  // O(1) lookup: real asset for `${model}__${category}` (if uploaded).
  // Multi-file support (2026-06-24): a (model, category) cell can hold
  // multiple labelled variants. The Submittal Package historically expected
  // a single PDF per category, so we pick the MOST RECENTLY UPLOADED variant
  // here as the default. Engineers who need a specific variant (e.g. 2-pipe
  // vs 4-pipe drawing) should delete the unwanted variants in the Admin tab
  // until the desired one is the newest, or re-upload the preferred one
  // (the new upload bumps it to the top). A future change can surface a
  // per-row variant picker in this tab — for now this preserves existing
  // single-file behavior.
  const realAssetByKey = useMemo(() => {
    const m = new Map();
    uploadedAssets.forEach((a) => {
      const k = `${a.model}__${a.category}`;
      const prev = m.get(k);
      if (!prev || (a.uploaded_at || "") > (prev.uploaded_at || "")) {
        m.set(k, a);
      }
    });
    return m;
  }, [uploadedAssets]);

  // Stored report blobs are fetched on demand (preview, compile) from the
  // backend — keeps payload light when the user only browses the tab.
  const fetchStoredReportBlob = (kind) => async () => {
    const rec = reports?.[kind];
    if (!rec) throw new Error(`No ${kind} report generated yet`);
    return downloadProjectReport(rec.id);
  };

  // The 3 dynamic files map to the persisted reports.
  // Each row reflects whether the report has been generated yet.
  // - Available  → row is enabled, preview/compile pulls the stored blob
  // - Missing    → row is disabled, "Generate in <step>" hint shown
  const dynamicFiles = useMemo(() => [
    {
      id: "dyn:selection-summary",
      label: "Selection Report",
      detail: reports.selection
        ? `${reports.selection.filename} · ${(reports.selection.size_bytes / 1024).toFixed(0)} KB · generated ${new Date(reports.selection.generated_at).toLocaleString()}`
        : "Not generated yet — go to Summary tab",
      filename: "SelectionReport",
      kind: "selection",
      available: !!reports.selection,
      regenStep: 3, // Summary tab
      generator: fetchStoredReportBlob("selection"),
    },
    {
      id: "dyn:config-report",
      label: "Config Report",
      detail: reports.config
        ? `${reports.config.filename} · ${(reports.config.size_bytes / 1024).toFixed(0)} KB · generated ${new Date(reports.config.generated_at).toLocaleString()}`
        : "Not generated yet — go to Schedule tab",
      filename: "ConfigReport",
      kind: "config",
      available: !!reports.config,
      regenStep: 5, // Schedule tab
      generator: fetchStoredReportBlob("config"),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [reports]);

  // Static files grouped per model. If the admin has uploaded a real PDF for
  // (model, category), use it (Official badge); otherwise fall back to the
  // client-generated placeholder stub.
  const staticGroups = useMemo(() => projectModels.map((m) => ({
    model: m,
    files: STATIC_CATEGORIES.map((c) => {
      const real = realAssetByKey.get(`${m}__${c.key}`);
      if (real) {
        return {
          id: `stat:${m}:${c.key}`,
          label: c.label,
          detail: `${m} · ${real.filename}`,
          filename: real.filename.replace(/\.pdf$/i, ""),
          available: true,
          isStub: false,
          // Fetch the real PDF from the backend as a Blob so the merge/preview
          // pipeline can treat all rows uniformly.
          generator: () => axios
            .get(`${API}/model-assets/${real.id}/download`, { responseType: "blob" })
            .then((r) => r.data),
        };
      }
      return {
        id: `stat:${m}:${c.key}`,
        label: c.label,
        detail: m,
        filename: `${m.replace(/\s+/g, "_")}_${c.key}`,
        available: true,
        isStub: true,
        generator: () => buildStubPdf({ project, modelLabel: m, categoryLabel: c.label }),
      };
    }),
  })), [projectModels, project, realAssetByKey]);

  // Submittal drawings — one entry per project model, each with its own checkbox.
  // Admin-uploaded drawing takes priority over the hardcoded /public/ fallback.
  const drawingsFiles = useMemo(() => {
    const wantedCat = isSI ? "drawing_si" : "drawing_ip";
    const fallbackCat = isSI ? "drawing_ip" : "drawing_si";
    const variantLabel = isSI ? "SI" : "IP";

    return projectModels.map((m) => {
      // 1) Admin-uploaded for this model (preferred variant, then fallback variant).
      const admin =
        realAssetByKey.get(`${m}__${wantedCat}`) ||
        realAssetByKey.get(`${m}__${fallbackCat}`) ||
        null;

      // 2) Hardcoded /public/submittal-drawings/ fallback for this single model.
      //    drawingsForRooms accepts a rooms array — wrap the model in a stub room.
      const hardcoded = admin ? [] : drawingsForRooms([{ unit_model: m }], isSI);

      if (!admin && hardcoded.length === 0) return null; // no drawing available for this model

      return {
        id: `drawings:${m}`,
        label: `Submittal Drawing — ${m}`,
        detail: admin
          ? `${m} · ${admin.filename} · uploaded ${new Date(admin.uploaded_at || admin.updated_at || Date.now()).toLocaleDateString()}`
          : `${m} · standard drawing (${variantLabel})`,
        filename: `Submittal_Drawing_${m.replace(/\s+/g, "_")}_${variantLabel}`,
        available: true,
        isStub: false,
        kind: "drawings",
        model: m,
        generator: async () => {
          if (admin) {
            // Single admin-uploaded PDF — just return the blob as-is.
            return axios
              .get(`${API}/model-assets/${admin.id}/download`, { responseType: "blob" })
              .then((r) => r.data);
          }
          // Hardcoded fallback — concatenate (usually a single file per model anyway).
          const merged = await PDFDocument.create();
          for (const d of hardcoded) {
            try {
              const bytes = await fetchDrawingBytes(d.url);
              const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
              const pages = await merged.copyPages(src, src.getPageIndices());
              pages.forEach((p) => merged.addPage(p));
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn(`Failed to load hardcoded drawing ${d.file}:`, e);
            }
          }
          const bytes = await merged.save();
          return new Blob([bytes], { type: "application/pdf" });
        },
      };
    }).filter(Boolean); // remove nulls (models with no drawing available)
  }, [projectModels, realAssetByKey, isSI]);

  // Build a flat lookup so compile/preview can resolve a file by id quickly.
  const allFiles = useMemo(() => {
    const flat = [...dynamicFiles];
    flat.push(...drawingsFiles);
    staticGroups.forEach((g) => flat.push(...g.files));
    return flat;
  }, [dynamicFiles, drawingsFiles, staticGroups]);

  // Default selection: all available dynamic files.
  //
  // `dynamicFiles` is derived from `reports` which loads asynchronously after
  // mount, so the initial state runs with an empty list. We re-seed on the
  // first render where any dynamic file becomes available, but only if the
  // user hasn't manually toggled anything yet (tracked via userTouchedRef).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const userTouchedRef = useRef(false);
  useEffect(() => {
    if (userTouchedRef.current) return;
    const s = new Set();
    dynamicFiles.forEach((f) => { if (f.available) s.add(f.id); });
    // Default every per-model drawings row to checked so engineers don't have to opt-in.
    drawingsFiles.forEach((f) => s.add(f.id));
    setSelectedIds(s);
  }, [dynamicFiles, drawingsFiles]);

  const [outputFormat, setOutputFormat] = useState("pdf"); // "pdf" | "zip"
  const [compileBusy, setCompileBusy] = useState(false);
  const [previewBusyId, setPreviewBusyId] = useState(null);

  const toggle = (id) => {
    userTouchedRef.current = true;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    userTouchedRef.current = true;
    const s = new Set();
    allFiles.forEach((f) => { if (f.available) s.add(f.id); });
    setSelectedIds(s);
  };
  const clearAll = () => {
    userTouchedRef.current = true;
    setSelectedIds(new Set());
  };

  // Open the file in a new tab via blob URL. Used by the eye-icon preview.
  const handlePreview = async (file) => {
    if (!file.available) return;
    setPreviewBusyId(file.id);
    try {
      const blob = await file.generator();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Browser blocked the preview window — allow popups for this site");
      }
      // Revoke after a delay so the new tab has time to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(`Preview failed: ${e?.message || e}`);
    } finally {
      setPreviewBusyId(null);
    }
  };

  // Compile selected files → either a polished merged PDF (cover + TOC +
  // selected docs + standard drawings) or a flat ZIP archive.
  const handleCompile = async () => {
    const chosen = allFiles.filter((f) => selectedIds.has(f.id) && f.available);
    if (chosen.length === 0) {
      toast.error("Select at least one file to compile");
      return;
    }
    setCompileBusy(true);
    try {
      // Generate every selected file in parallel — IO-bound, so this is fast.
      const blobs = await Promise.all(
        chosen.map(async (f) => ({ file: f, blob: await f.generator() }))
      );
      const safeProject = (project?.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
      const stamp = new Date().toISOString().slice(0, 10);

      if (outputFormat === "pdf") {
        // -------- Merged PDF: Cover (Title) + TOC + ordered docs + standard
        // submittal drawings + remaining user-ticked extras.
        //
        // Order is enforced regardless of how the user ticks them:
        //   1. Selection Report   (kind=selection)
        //   2. Config Report      (kind=config)
        //   3. Submittal Drawing  (standard FMTBY/FMLBY/etc. drawings)
        //   4+. Any other ticked items in user order
        const isSelection = (entry) => entry.file.kind === "selection";
        const isConfig = (entry) => entry.file.kind === "config";
        const isDrawings = (entry) => entry.file.kind === "drawings";
        // Reuse the already-fetched blobs (no double work) but enforce order:
        //   1. Selection Report  2. Config Report  3. Submittal Drawings  4+. others
        const orderedBlobs = [
          ...blobs.filter(isSelection),
          ...blobs.filter(isConfig),
          ...blobs.filter(isDrawings),
        ];
        const otherBlobs = blobs.filter(
          (b) => !isSelection(b) && !isConfig(b) && !isDrawings(b)
        );

        const out = await PDFDocument.create();
        out.setTitle(`${project?.name || "Project"} — Submittal Package`);
        out.setAuthor("ZAMBA Selection Tool");
        out.setCreator("ZAMBA Selection Tool");
        out.setProducer("ZAMBA Selection Tool");

        const helv = await out.embedFont(StandardFonts.Helvetica);
        const helvBold = await out.embedFont(StandardFonts.HelveticaBold);

        const A4 = [595.28, 841.89];

        // ----- Page 1: Cover Page (same look as Selection / Config reports) -----
        try {
          const coverBlob = await generateSubmittalCoverPdf({
            project, rooms, results,
            coverTitle: "Submittal Package",
            isSI,
          });
          const coverBytes = await coverBlob.arrayBuffer();
          const coverDoc = await PDFDocument.load(coverBytes);
          const [coverPage] = await out.copyPages(coverDoc, [0]);
          out.addPage(coverPage);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("Cover render failed, falling back to blank cover:", e);
          out.addPage(A4);
        }

        // ----- Page 2: TOC placeholder (filled in once we know page numbers) -----
        const tocPage = out.addPage(A4);

        const sections = []; // {label, page}

        // ----- 1. Selection Report  +  2. Config Report -----
        for (const { file, blob } of orderedBlobs) {
          const startPage = out.getPageCount() + 1;
          try {
            const buf = await blob.arrayBuffer();
            const src = await PDFDocument.load(buf, { ignoreEncryption: true });
            const pages = await out.copyPages(src, src.getPageIndices());
            pages.forEach((p) => out.addPage(p));
            sections.push({ label: file.label, page: startPage });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`Failed to merge ${file.label}:`, e);
            toast.warning(`Skipped ${file.label} during merge (unreadable PDF)`);
          }
        }

        // ----- 4+. Remaining user-ticked extras (Wiring, P&ID, Brochures, …) -----
        // NOTE: Submittal Drawings are now a regular ticked file (kind="drawings")
        // and flow through orderedBlobs above — no unconditional block needed.
        for (const { file, blob } of otherBlobs) {
          const startPage = out.getPageCount() + 1;
          try {
            const buf = await blob.arrayBuffer();
            const src = await PDFDocument.load(buf, { ignoreEncryption: true });
            const pages = await out.copyPages(src, src.getPageIndices());
            pages.forEach((p) => out.addPage(p));
            sections.push({ label: file.label, page: startPage });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`Failed to merge ${file.label}:`, e);
            toast.warning(`Skipped ${file.label} during merge (unreadable PDF)`);
          }
        }

        // Now that every section's page is known, paint the TOC.
        drawTocPage(tocPage, project, sections, helv, helvBold);

        const mergedBytes = await out.save();
        const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });
        const filename = `ZAMBA_${safeProject}_SubmittalPackage_${stamp}.pdf`;
        const url = URL.createObjectURL(mergedBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
        // Persist the final submittal so the engineer (or another team
        // member) can re-download it later without recompiling.
        if (project?.id) {
          try {
            await uploadProjectReport(project.id, "submittal", mergedBlob, filename);
            if (onReportGenerated) onReportGenerated("submittal");
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Submittal upload to backend failed:", e);
          }
        }
        toast.success(
          `Submittal Package compiled (${sections.length} section${sections.length === 1 ? "" : "s"}, ${out.getPageCount()} pages)`
        );
      } else {
        // -------- ZIP: just bundle the selected files + standard drawings.
        // No title/TOC since each file stays standalone inside the archive.
        // ZIP: drawings are now a regular ticked file — the generator returns a
        // single merged-drawings blob that is added like any other entry.
        const zip = new JSZip();
        for (const { file, blob } of blobs) {
          zip.file(`${file.filename}.pdf`, blob);
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ZAMBA_${safeProject}_SubmittalPackage_${stamp}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
        toast.success(`ZIP created (${chosen.length} document${chosen.length === 1 ? "" : "s"})`);
      }
    } catch (e) {
      toast.error(`Compile failed: ${e?.message || e}`);
    } finally {
      setCompileBusy(false);
    }
  };

  const FileRow = ({ file }) => {
    const checked = selectedIds.has(file.id);
    const disabled = !file.available;
    const isDynamic = !!file.kind; // selection / config
    return (
      <div
        className={`flex items-center gap-3 py-2.5 px-3 rounded-md border ${
          disabled ? "bg-amber-500/5 border-amber-200 dark:border-amber-800" : "bg-card border-border hover:bg-muted/50"
        } transition-colors`}
        data-testid={`submittal-row-${file.id}`}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => toggle(file.id)}
          disabled={disabled}
          data-testid={`submittal-cb-${file.id}`}
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={disabled || previewBusyId === file.id}
          onClick={() => handlePreview(file)}
          title={disabled ? "Not available" : "Preview"}
          data-testid={`submittal-preview-${file.id}`}
        >
          {previewBusyId === file.id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground truncate">
            {file.label}
            {file.isStub === true && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                <FileWarning className="h-3 w-3" /> Placeholder
              </span>
            )}
            {file.isStub === false && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                <FileCheck2 className="h-3 w-3" /> Official
              </span>
            )}
            {isDynamic && file.available && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                <FileCheck2 className="h-3 w-3" /> Generated
              </span>
            )}
            {isDynamic && !file.available && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                <AlertTriangle className="h-3 w-3" /> Not generated
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{file.detail}</div>
        </div>
        {isDynamic && onJumpToStep && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onJumpToStep(file.regenStep)}
            data-testid={`submittal-regen-${file.kind}`}
            title={file.available ? "Re-generate from source tab" : "Go to the source tab to generate"}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {file.available ? "Regenerate" : "Generate"}
          </Button>
        )}
        {disabled && !isDynamic && (
          <span className="text-xs text-muted-foreground italic">Not available</span>
        )}
      </div>
    );
  };

  const selectedCount = [...selectedIds].filter((id) => allFiles.find((f) => f.id === id && f.available)).length;

  return (
    <Card data-testid="submittal-package-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" /> Submittal Package
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Reports below are <strong className="text-foreground">pulled from the database</strong> — generate
          them in the Summary &amp; Schedule tabs before compiling. The
          compiler does not re-run the per-room engine. ZAMBA auto-adds a{" "}
          <strong className="text-foreground">Title page</strong> and a <strong className="text-foreground">Table of Contents</strong>.
          Admin-uploaded drawings appear in the <strong className="text-foreground">Submittal Drawings</strong> row
          (default ON); uncheck to omit them.
          Items marked{" "}
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded align-middle">
            <FileWarning className="h-3 w-3" /> Placeholder
          </span>{" "}
          are stub PDFs awaiting upload of the real model documentation.
        </p>
        {reports?.submittal && (
          <div
            className="mt-2 flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800 px-2 py-1.5 rounded"
            data-testid="submittal-saved-status"
          >
            <FileCheck2 className="h-3.5 w-3.5" />
            <span className="font-medium">Submittal Package on file:</span>
            <span className="truncate">{reports.submittal.filename}</span>
            <span className="text-muted-foreground">·</span>
            <span>{(reports.submittal.size_bytes / 1024 / 1024).toFixed(2)} MB</span>
            <span className="text-muted-foreground">·</span>
            <span>{new Date(reports.submittal.generated_at).toLocaleString()}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 ml-auto text-xs"
              data-testid="submittal-download-saved"
              onClick={async () => {
                try {
                  const blob = await downloadProjectReport(reports.submittal.id);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = reports.submittal.filename;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 5_000);
                } catch (e) {
                  toast.error(`Download failed: ${e?.message || e}`);
                }
              }}
            >
              Download
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-border">
          <Button size="sm" variant="outline" onClick={selectAll} data-testid="submittal-select-all">
            Select All
          </Button>
          <Button size="sm" variant="outline" onClick={clearAll} data-testid="submittal-clear-all">
            Clear
          </Button>
          <div className="flex-1" />
          <span className="text-sm text-muted-foreground">
            {selectedCount} of {allFiles.filter((f) => f.available).length} selected
          </span>
        </div>

        {/* Section 1 — Project Documents (dynamic) */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Project Documents
          </div>
          <div className="space-y-1.5">
            {dynamicFiles.map((f) => <FileRow key={f.id} file={f} />)}
          </div>
        </div>

        {/* Section 2 — Submittal Drawings (one row per project model, default ON) */}
        {drawingsFiles.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Submittal Drawings ({isSI ? "SI" : "IP"})
            </div>
            <div className="space-y-1.5">
              {drawingsFiles.map((f) => (
                <FileRow key={f.id} file={f} />
              ))}
            </div>
          </div>
        )}

        {/* Section 3..N — Per-model groups (static) */}
        {staticGroups.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            No model documentation available — add rooms in Step 2 to populate this section.
          </div>
        ) : (
          staticGroups.map((g) => (
            <div key={g.model} data-testid={`submittal-group-${g.model.replace(/\s+/g, "_")}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {g.model} Documents
              </div>
              <div className="space-y-1.5">
                {g.files.map((f) => <FileRow key={f.id} file={f} />)}
              </div>
            </div>
          ))
        )}

        {/* Compile bar */}
        <div className="pt-4 border-t border-border flex flex-wrap items-center gap-4">
          <RadioGroup
            value={outputFormat}
            onValueChange={setOutputFormat}
            className="flex items-center gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="pdf" id="fmt-pdf" data-testid="submittal-fmt-pdf" />
              <Label htmlFor="fmt-pdf" className="text-sm cursor-pointer">Merged PDF</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="zip" id="fmt-zip" data-testid="submittal-fmt-zip" />
              <Label htmlFor="fmt-zip" className="text-sm cursor-pointer">ZIP archive</Label>
            </div>
          </RadioGroup>
          <div className="flex-1" />
          <Button
            onClick={handleCompile}
            disabled={compileBusy || selectedCount === 0}
            className="bg-foreground text-background hover:opacity-90"
            data-testid="submittal-compile-btn"
          >
            {compileBusy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Package className="h-4 w-4 mr-2" />
            )}
            {compileBusy ? "Compiling…" : `Compile Package (${selectedCount})`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
